import PropTypes from 'prop-types';
import { connect, ConnectedProps } from 'react-redux';
import { withRouter } from 'next/router';
import {
  compose,
  renderComponent,
  withProps,
  branch,
  mapProps,
  lifecycle,
} from 'recompose';
import inviterApi from './api';
import * as redux from './redux';
import InviterDataPropType from './propType';
import {
  makeError,
  makeErrorFromApi,
  UNKNOWN_ERROR,
  ErrorType,
  ApiError,
} from 'lib/error';
import withErrorHandler, {
  WithErrorHandlerPropType,
  WithErrorHandlerProps,
  isErrorOfTypeAndCode,
} from 'data/error/withErrorHandler';
import GeneralError from 'containers/Error/GeneralError';
import JoinOwnPlan from 'containers/Error/JoinOwnPlan';
import UserHasPlanHopped from 'containers/Error/UserHasPlanHopped';
import UserHasFailedReverification from 'containers/Error/UserHasFailedReverification';
import PlanAlreadyFullError from 'containers/Error/PlanAlreadyFullError';
import InvitationExpiredError from 'containers/Error/InvitationExpiredError';
import withRedirect from 'data/redirect/withRedirect';
import buildUrl from 'lib/url';
import { setHomeId, setUserType } from 'lib/tracking';
import { InviteToken } from 'types';
import { ComponentType } from 'react';

type WithInviterOuterProps = ConnectedProps<typeof connector> & { router: any };
type WithInviterErrorProps = WithInviterOuterProps & WithErrorHandlerProps;

export type WithInviterProps = Omit<
  WithInviterErrorProps,
  'isLoading' | 'data' | 'dispatch'
> & {
  inviteToken: InviteToken;
  loadInviter: (inviteToken: InviteToken) => Promise<void>;
  isLoadingInviter: boolean;
  inviterData: WithInviterOuterProps['data'];
  showErrorOnNoInvite?: boolean;
};

type InviterDataProp = Pick<WithInviterProps, 'inviterData'>;
type ErrorProp = Pick<WithInviterProps, 'error'>;

export const LOAD_INVITER_ERROR_TYPE: ErrorType = 'LOAD_INVITER_ERROR_TYPE';

type LoadInviterErrorCode =
  | 'PLAN_IS_FULL'
  | 'INVITE_NOT_FOUND'
  | 'USER_ALREADY_JOINED'
  | 'MASTER_JOIN_OWN_PLAN'
  | 'USER_HAS_PLAN_HOPPED'
  | 'USER_HAS_FAILED_REVERIFICATION';

/**
 * @deprecated use WithInviterProps instead
 */
export const WithInviterPropType = {
  isLoadingInviter: PropTypes.bool.isRequired,
  inviterData: InviterDataPropType,
  loadInviter: PropTypes.func.isRequired,
  inviteToken: PropTypes.string.isRequired,
  ...WithErrorHandlerPropType,
};

/**
 * @deprecated use LoadInviterErrorCode instead
 */
export const loadInviterErrors: Record<string, LoadInviterErrorCode> = {
  PLAN_IS_FULL: 'PLAN_IS_FULL',
  INVITE_NOT_FOUND: 'INVITE_NOT_FOUND',
  USER_ALREADY_JOINED: 'USER_ALREADY_JOINED',
  MASTER_JOIN_OWN_PLAN: 'MASTER_JOIN_OWN_PLAN',
  USER_HAS_PLAN_HOPPED: 'USER_HAS_PLAN_HOPPED',
  USER_HAS_FAILED_REVERIFICATION: 'USER_HAS_FAILED_REVERIFICATION',
};

export const loadInviter =
  (dispatch: WithInviterOuterProps['dispatch']) =>
  async (inviteToken: InviteToken) => {
    dispatch(redux.loadInviterData());
    try {
      const data = await inviterApi.loadInviter(inviteToken);
      setHomeId(data.homeId);
      setUserType(data.isLoggedInUserMaster ? 'master' : 'sub');
      dispatch(redux.setInviterData(data));
    } catch (e: any) {
      // Resolve the home to empty string in case of an error
      setUserType('');
      setHomeId('');
      dispatch(redux.setInviterData(null));

      throw makeErrorFromApi(LOAD_INVITER_ERROR_TYPE, e);
    }
  };

const renderComponentWithLoadError = <T>(
  errorCode: LoadInviterErrorCode,
  Component: ComponentType<T & { error: ApiError }>,
) =>
  renderComponent(
    withProps(() => ({
      error: makeError(LOAD_INVITER_ERROR_TYPE, errorCode),
    }))(Component),
  );

const hasRestriction = (
  inviterData: redux.UserData | null,
  reason: redux.RestrictionReason,
) =>
  inviterData?.joinRestrictions?.some(
    (restriction: redux.JoinRestriction) => restriction.reason === reason,
  ) || false;

export const withInviter = (
  { showUserSpecificErrors, showErrorOnNoInvite } = {
    showUserSpecificErrors: true,
    showErrorOnNoInvite: true,
  },
) =>
  compose<WithInviterProps, WithInviterOuterProps>(
    withErrorHandler,
    withRouter,
    mapProps<WithInviterProps, WithInviterErrorProps>(
      ({ isLoading, data, ...props }: WithInviterErrorProps) => ({
        ...props,
        inviteToken: props.router.query.token,
        loadInviter: loadInviter(props.dispatch),
        isLoadingInviter: isLoading,
        inviterData: data,
      }),
    ),
    lifecycle<WithInviterProps, {}>({
      componentDidMount() {
        if (!this.props.inviterData) {
          this.props
            .loadInviter(this.props.inviteToken)
            .catch(this.props.setError);
        }
      },
    }),
    withRedirect(
      // @ts-ignore
      (props: WithInviterProps) => buildUrl(props, 'HOME_HUB'),
      ({ error }: ErrorProp) =>
        isErrorOfTypeAndCode(
          error,
          LOAD_INVITER_ERROR_TYPE,
          'USER_ALREADY_JOINED',
        ),
    ),
    branch(
      ({ error }: ErrorProp) =>
        isErrorOfTypeAndCode(error, LOAD_INVITER_ERROR_TYPE, 'PLAN_IS_FULL'),
      renderComponent(PlanAlreadyFullError),
    ),
    branch(
      ({ error }: ErrorProp) =>
        isErrorOfTypeAndCode(
          error,
          LOAD_INVITER_ERROR_TYPE,
          'INVITE_NOT_FOUND',
        ),
      renderComponent(InvitationExpiredError),
    ),
    branch(
      ({ inviterData }: InviterDataProp) =>
        showUserSpecificErrors && (inviterData?.isLoggedInUserMaster || false),
      renderComponentWithLoadError('MASTER_JOIN_OWN_PLAN', JoinOwnPlan),
    ),
    branch(
      ({ inviterData }: InviterDataProp) =>
        showUserSpecificErrors &&
        (inviterData?.planHoppingRestrictionsApply ||
          hasRestriction(inviterData, 'PLAN_HOPPED')),
      renderComponentWithLoadError('USER_HAS_PLAN_HOPPED', UserHasPlanHopped),
    ),
    branch(
      ({ inviterData }: InviterDataProp) =>
        showUserSpecificErrors &&
        hasRestriction(inviterData, 'FAILED_REVERIFICATION'),
      renderComponentWithLoadError(
        'USER_HAS_FAILED_REVERIFICATION',
        UserHasFailedReverification,
      ),
    ),
    branch(
      (props: WithInviterProps) =>
        showErrorOnNoInvite &&
        isErrorOfTypeAndCode(
          props.error,
          LOAD_INVITER_ERROR_TYPE,
          UNKNOWN_ERROR,
        ),
      renderComponent(GeneralError),
    ),
  );

const connector = connect(redux.selector);

const withInviterHoc =
  (
    {
      showUserSpecificErrors = true,
      showErrorOnNoInvite = true,
    }: { showUserSpecificErrors: boolean; showErrorOnNoInvite: boolean } = {
      showUserSpecificErrors: true,
      showErrorOnNoInvite: true,
    },
  ) =>
  (WrappedComponent: ComponentType<WithInviterProps>) =>
    connector(
      withInviter({ showUserSpecificErrors, showErrorOnNoInvite })(
        WrappedComponent,
      ),
    );

export default withInviterHoc;
